import { Connection, PublicKey } from "@solana/web3.js";
import { Raydium } from "@raydium-io/raydium-sdk-v2";
import type { Cluster } from "@oxminter/shared/cluster";
import type { ConnectedWallet, AnyTx } from "./wallet";

/**
 * The SDK unconditionally console.log()s a base64 dump of every transaction it
 * executes ("simulate tx string:"). Harmless but it floods the browser console
 * and buries real errors, so it is filtered at the source.
 */
export function installSdkLogFilter(): void {
  const w = window as unknown as { __sdkLogFiltered?: boolean };
  if (w.__sdkLogFiltered) return;
  const original = console.log.bind(console);
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === "string" && args[0].startsWith("simulate tx string")) return;
    original(...args);
  };
  w.__sdkLogFiltered = true;
}

export async function loadRaydium(
  connection: Connection,
  cluster: Cluster,
  wallet: ConnectedWallet
): Promise<Raydium> {
  installSdkLogFilter();
  return Raydium.load({
    connection,
    cluster: cluster === "devnet" ? "devnet" : "mainnet",
    owner: wallet.publicKey as PublicKey,
    // Both of these hit Raydium's mainnet HTTP API. Skipping them keeps devnet
    // and localnet working when that API is unreachable, and keeps this app's
    // behaviour identical to the CLI's.
    disableLoadToken: true,
    disableFeatureCheck: true,
    blockhashCommitment: "confirmed",
    signAllTransactions: (async (txs: AnyTx[]) =>
      wallet.signAllTransactions(txs)) as Parameters<
      typeof Raydium.load
    >[0]["signAllTransactions"],
  });
}

/**
 * A pool account is not always readable the instant its creation transaction
 * confirms: the RPC node may not serve it for a few slots. Retry rather than
 * report a successful launch as a failed one.
 */
export async function getPoolInfoWithRetry<T>(
  fetcher: () => Promise<T>,
  attempts = 12,
  delayMs = 2500
): Promise<T> {
  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fetcher();
    } catch (e) {
      lastError = e;
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  throw new Error(
    `pool state was not readable after ${attempts} attempts: ${(lastError as Error)?.message}`
  );
}
